import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { fmtNumber, fmtRupiah, STATUS_META } from "@/lib/api";

const TooltipStyle = { background: "#0B132B", border: "1px solid #334155", borderRadius: 10, color: "#F8FAFC", fontSize: 12, padding: 8 };
export const DEPT_PALETTE = ["#F59E0B", "#06B6D4", "#8B5CF6", "#10B981", "#EF4444", "#38BDF8", "#F97316", "#A78BFA", "#22D3EE", "#FB7185"];

const StatusTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const s = payload[0].payload;
  return <div style={TooltipStyle}>{STATUS_META[s.name]?.label || s.name}: <span className="font-mono font-semibold">{s.value} item</span></div>;
};

const MiniDonut = ({ dept }) => (
  <div className="w-[42%] max-w-[220px] aspect-square relative flex-shrink-0">
    <ResponsiveContainer>
      <PieChart>
        <Pie data={dept.statuses || []} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="95%" paddingAngle={2}>
          {(dept.statuses || []).map((s) => <Cell key={s.name} fill={STATUS_META[s.name]?.hex || "#64748B"} stroke="#0B132B" />)}
        </Pie>
        <Tooltip content={<StatusTooltip />} wrapperStyle={{ zIndex: 20 }} />
      </PieChart>
    </ResponsiveContainer>
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <div className="text-xl sm:text-2xl font-extrabold text-slate-50">{fmtNumber(dept.count)}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">item</div>
    </div>
  </div>
);

const StatusList = ({ dept }) => (
  <div className="flex-1 min-w-0 space-y-1.5">
    {(dept.statuses || []).map((s) => (
      <div key={s.name} data-testid={`dept-status-${dept.name}-${s.name}`} className="text-[11px] border-b border-slate-800/70 pb-1 last:border-0">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-slate-300 truncate"><span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: STATUS_META[s.name]?.hex }} />{STATUS_META[s.name]?.label || s.name}</span>
          <span className="font-mono text-slate-100 flex-shrink-0">{fmtNumber(s.value)} item <span className="text-slate-500">({pct(s.value, dept.count)}%)</span></span>
        </div>
        <div className="text-right font-mono text-[10px] text-emerald-300/80">Total {fmtRupiah(s.amount)}</div>
      </div>
    ))}
  </div>
);

const pct = (v, t) => (t ? Math.round((v / t) * 100) : 0);

const OpenClose = ({ dept }) => (
  <div className="grid grid-cols-2 gap-2 mt-3">
    <div data-testid={`dept-open-${dept.name}`} className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 flex items-center justify-between">
      <span className="text-[9px] uppercase tracking-widest text-amber-300/80">Open</span>
      <span className="text-sm font-extrabold text-amber-300 font-mono">{fmtNumber(dept.open)} <span className="text-[11px] font-semibold text-amber-300/70">({pct(dept.open, dept.count)}%)</span></span>
    </div>
    <div data-testid={`dept-close-${dept.name}`} className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 flex items-center justify-between">
      <span className="text-[9px] uppercase tracking-widest text-emerald-300/80">Close</span>
      <span className="text-sm font-extrabold text-emerald-300 font-mono">{fmtNumber(dept.close)} <span className="text-[11px] font-semibold text-emerald-300/70">({pct(dept.close, dept.count)}%)</span></span>
    </div>
  </div>
);

const DeptCard = ({ dept, color }) => (
  <div data-testid={`dept-card-${dept.name}`} className="rounded-lg border border-slate-700/60 bg-[#0B132B]/60 p-3 transition-colors hover:border-slate-500/70">
    <div className="flex items-center justify-between mb-1 gap-2">
      <span className="flex items-center gap-2 text-sm font-semibold text-slate-100 truncate"><span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: color }} /><span className="truncate">{dept.name}</span></span>
      <span className="text-[11px] font-mono text-slate-400 flex-shrink-0">{fmtNumber(dept.count)} item · {dept.pct}%</span>
    </div>
    <div className="flex items-center gap-3">
      <MiniDonut dept={dept} />
      <StatusList dept={dept} />
    </div>
    <OpenClose dept={dept} />
  </div>
);

export const DeptBar = ({ data }) => (
  <div data-testid="dept-donut-card" className="rounded-xl border border-slate-700/60 bg-[#111D38]/70 backdrop-blur-md p-5 h-full">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-slate-100 font-semibold text-base">Monitoring Status Order All Department</h3>
      <span className="text-[10px] uppercase tracking-widest text-slate-500">Order Status</span>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
      {data.map((d, i) => <DeptCard key={d.name} dept={d} color={DEPT_PALETTE[i % DEPT_PALETTE.length]} />)}
      {!data.length && <div data-testid="dept-empty" className="col-span-full py-10 text-center text-sm text-slate-500">Tidak ada data pada periode ini</div>}
    </div>
  </div>
);
